import { Injectable, OnDestroy, computed, signal } from '@angular/core';

export type TopbarMenuId = 'create' | 'bell' | 'tenant' | 'language' | 'user';

/**
 * Which top-bar popover is open (NBO-2331). One token for all five clusters,
 * mirroring the single `menu` state in the canonical mockup.
 *
 * Two jobs beyond bookkeeping:
 *
 * 1. **Mutual exclusion is explicit**, not a side effect of the CDK backdrop.
 * 2. **Switching between triggers takes ONE click.** Each overlay has a
 *    transparent backdrop, which swallows the click that would otherwise reach
 *    a sibling trigger — so without `handleBackdropClick` the user has to click
 *    twice to move from one menu to the next (verified live). The handler
 *    looks at what sits under the pointer and, if it is another trigger, opens
 *    that menu directly.
 */
@Injectable({ providedIn: 'root' })
export class TopbarMenuService implements OnDestroy {
  private readonly openMenu = signal<TopbarMenuId | null>(null);

  readonly current = this.openMenu.asReadonly();

  constructor() {
    document.addEventListener('keydown', this.onKeydown);
  }

  isOpen(id: TopbarMenuId): boolean {
    return this.openMenu() === id;
  }

  /** Signal factory so templates can bind without a function call per CD pass. */
  openFor(id: TopbarMenuId) {
    return computed(() => this.openMenu() === id);
  }

  toggle(id: TopbarMenuId): void {
    this.openMenu.update((current) => (current === id ? null : id));
  }

  close(): void {
    this.openMenu.set(null);
  }

  /**
   * Close only if `id` is the menu currently open. Bound to each overlay's
   * `(detach)`: a bare close() there would race `handleBackdropClick`, whose
   * deferred open would be cancelled by the OLD overlay's detach landing after
   * it — clicking straight from one trigger to another would then open nothing.
   */
  closeIf(id: TopbarMenuId): void {
    if (this.openMenu() === id) {
      this.openMenu.set(null);
    }
  }

  /**
   * Close, then hand the click to whatever trigger was underneath the backdrop.
   * Triggers opt in with `data-topbar-trigger="<id>"`.
   */
  handleBackdropClick(event: MouseEvent): void {
    const wasOpen = this.openMenu();
    this.close();

    // elementsFromPoint (plural) — the singular form returns the backdrop
    // itself, which is still in the DOM and covers the whole viewport, so the
    // trigger underneath is never found and the click is simply swallowed.
    const stack = document.elementsFromPoint(event.clientX, event.clientY);
    const trigger = stack
      .map((el) => el.closest<HTMLElement>('[data-topbar-trigger]'))
      .find((el): el is HTMLElement => !!el);
    const id = trigger?.dataset['topbarTrigger'] as TopbarMenuId | undefined;

    // Clicking the trigger of the menu that is already open means CLOSE.
    // Reopening it would make the control impossible to dismiss by its own
    // button — the click would toggle it off and straight back on.
    if (id && id !== wasOpen) {
      // Defer so this open lands after the current overlay finishes detaching.
      setTimeout(() => this.openMenu.set(id));
    }
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.openMenu() !== null) {
      this.close();
    }
  };

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.onKeydown);
  }
}
